import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { hotSearchCategories, hotSearchWords } from "@/mocks/hotsearch";
import { formatHeat } from "@/utils/format";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-accent-600",
  down: "ri-arrow-down-line text-secondary-700",
  flat: "ri-subtract-line text-foreground-300",
};

export default function HotSearch() {
  const [cat, setCat] = useState("全部");

  useDocumentMeta({
    title: "热门搜索 - 百度一下",
    description: "百度一下热门搜索榜单，实时查看各领域热搜关键词与热度趋势，点击即可进入综合搜索结果。",
    keywords: "百度一下热搜,热门搜索,热搜榜,实时热点,热词排行",
  });

  const list = useMemo(
    () => (cat === "全部" ? hotSearchWords : hotSearchWords.filter((w) => w.category === cat)),
    [cat],
  );

  const maxHeat = Math.max(...hotSearchWords.map((w) => w.heat));

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <div className="mx-auto max-w-4xl">
              <h1 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                <i className="ri-fire-line text-accent-500" />
                热门搜索
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                全网热搜榜单，点击任意关键词即可进入综合搜索结果。热度为演示数据示意。
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {hotSearchCategories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCat(c)}
                    className={`rounded-full px-3.5 py-1.5 text-sm whitespace-nowrap transition-colors ${
                      cat === c
                        ? "bg-primary-500 font-medium text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-10">
          <ul className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {list.map((w, idx) => (
              <li key={w.keyword} className="border-b border-background-200 last:border-b-0">
                <Link
                  to={`/search?q=${encodeURIComponent(w.keyword)}`}
                  className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-background-100 md:px-5"
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      idx < 3 && cat === "全部"
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    {idx + 1}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium text-foreground-950">{w.keyword}</p>
                      <span className="hidden rounded bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-900 sm:inline">
                        {w.category}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-primary-400"
                        style={{ width: `${Math.round((w.heat / maxHeat) * 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2 text-sm text-foreground-500">
                    <i className={trendIcon[w.trend]} />
                    <span className="whitespace-nowrap">{formatHeat(w.heat)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-xs text-foreground-400">
            演示范围，热搜数据为示例内容，待接入后端支持。
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}